import { useState, useMemo } from 'react'
import { useStudents, useUpdateRemarks } from '@/lib/teacher-queries'
import type { StudentBasicInfo } from '@/lib/teacher-queries'
import LoadingSpinner from '../LoadingSpinner'
import { getFileUrl } from '@/lib/pocketbase'
import { enqueueSnackbar } from 'notistack'

interface StudentListTableProps {
  onDistributeClick: (student: StudentBasicInfo) => void
}

const StudentListTable = ({ onDistributeClick }: StudentListTableProps) => {
  const { data: students, isLoading, error } = useStudents()
  const updateRemarksMutation = useUpdateRemarks()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'points'>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [editingStudent, setEditingStudent] = useState<StudentBasicInfo | null>(null)
  const [remarksValue, setRemarksValue] = useState('')

  const handleEditRemarks = (student: StudentBasicInfo) => {
    setEditingStudent(student)
    setRemarksValue(student.remarks || '')
  }

  const handleSaveRemarks = async () => {
    if (!editingStudent) return

    try {
      await updateRemarksMutation.mutateAsync({
        student_id: editingStudent.id,
        remarks: remarksValue,
      })
      enqueueSnackbar('Remarks updated successfully', { variant: 'success' })
      setEditingStudent(null)
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to update remarks', { variant: 'error' })
    }
  }

  const handleCloseModal = () => {
    setEditingStudent(null)
    setRemarksValue('')
  }

  // Filter and sort students
  const filteredAndSortedStudents = useMemo(() => {
    if (!students) return []

    let filtered = students.filter((student: StudentBasicInfo) => {
      const searchLower = searchTerm.toLowerCase()
      return (
        student.name?.toLowerCase().includes(searchLower) ||
        student.username?.toLowerCase().includes(searchLower) ||
        student.email?.toLowerCase().includes(searchLower)
      )
    })

    filtered.sort((a: StudentBasicInfo, b: StudentBasicInfo) => {
      let comparison = 0
      if (sortBy === 'name') {
        const nameA = a.name || a.username || ''
        const nameB = b.name || b.username || ''
        comparison = nameA.localeCompare(nameB)
      } else {
        comparison = (a.points_balance || 0) - (b.points_balance || 0)
      }
      return sortOrder === 'asc' ? comparison : -comparison
    })

    return filtered
  }, [students, searchTerm, sortBy, sortOrder])

  const toggleSort = (column: 'name' | 'points') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
  }

  if (isLoading) {
    return (
      <div className="card bg-white shadow-xl">
        <div className="card-body flex items-center justify-center min-h-[400px]">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card bg-white shadow-xl">
        <div className="card-body">
          <p className="text-error">Failed to load students: {error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="card bg-white shadow-xl">
      <div className="card-body">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          <h2 className="card-title text-primary">Students</h2>

          <div className="form-control w-full md:w-auto">
            <input
              type="text"
              placeholder="Search by name, username, or email..."
              className="input input-bordered w-full md:w-80"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {filteredAndSortedStudents.length === 0 ? (
          <div className="text-center py-8 text-base-content/60">
            {searchTerm ? 'No students found matching your search.' : 'No students found.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th>Avatar</th>
                  <th>
                    <button
                      className="flex items-center gap-1 hover:text-primary"
                      onClick={() => toggleSort('name')}
                    >
                      Name
                      {sortBy === 'name' && (
                        <span className="text-xs">
                          {sortOrder === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th>Username</th>
                  <th>Remarks</th>
                  <th>
                    <button
                      className="flex items-center gap-1 hover:text-primary"
                      onClick={() => toggleSort('points')}
                    >
                      Points Balance
                      {sortBy === 'points' && (
                        <span className="text-xs">
                          {sortOrder === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedStudents.map((student: StudentBasicInfo) => {
                  const avatarUrl = student.avatar
                    ? getFileUrl('users', student.id, student.avatar)
                    : null

                  return (
                    <tr key={student.id}>
                      <td>
                        <div className="avatar">
                          <div className="w-12 h-12 rounded-full">
                            {avatarUrl ? (
                              <img src={avatarUrl} alt={student.name || student.username} />
                            ) : (
                              <div className="bg-primary/10 w-full h-full flex items-center justify-center text-primary font-bold">
                                {(student.name || student.username || '?')[0].toUpperCase()}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="font-medium">{student.name || '-'}</td>
                      <td className="text-base-content/60">{student.username}</td>
                      <td className="text-base-content/60 max-w-xs">
                        <div className="flex items-center gap-1">
                          <span className="truncate">{student.remarks || '-'}</span>
                          <button
                            className="btn btn-xs btn-ghost"
                            onClick={() => handleEditRemarks(student)}
                            title="Edit remarks"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-primary badge-lg">
                          {student.points_balance?.toFixed(0) || 0} pts
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => onDistributeClick(student)}
                        >
                          Give Points
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-sm text-base-content/60 mt-4">
          Showing {filteredAndSortedStudents.length} of {students?.length || 0} students
        </div>
      </div>

      {/* Edit Remarks Modal */}
      {editingStudent && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              Edit Remarks for {editingStudent.name || editingStudent.username}
            </h3>
            <div className="form-control">
              <label className="label">
                <span className="label-text">Remarks</span>
              </label>
              <textarea
                className="textarea textarea-bordered h-32"
                placeholder="Enter remarks about this student..."
                value={remarksValue}
                onChange={(e) => setRemarksValue(e.target.value)}
              />
            </div>
            <div className="modal-action">
              <button
                className="btn btn-ghost"
                onClick={handleCloseModal}
                disabled={updateRemarksMutation.isPending}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveRemarks}
                disabled={updateRemarksMutation.isPending}
              >
                {updateRemarksMutation.isPending ? (
                  <span className="loading loading-spinner loading-sm"></span>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={handleCloseModal}></div>
        </div>
      )}
    </div>
  )
}

export default StudentListTable
